import React from "react";
import { Brain } from "lucide-react";
import { Hero, Section, Sub, Callout, Quote, Bold } from "./_shared";

export default function MindsetPage() {
  return (
    <div data-testid="view-mindset" className="max-w-5xl">
      <Hero
        moduleNum="A → J"
        eyebrowLabel="Stratégie · Mindset"
        icon={Brain}
        italic="Mindset"
        accent="du deal maker"
        description="10 sous-modules complets sur la posture mentale, le personal branding, le réseau, l'art de closer, la structuration avancée, l'intelligence des marchés, le droit des contrats, la gestion de crise, les finances et l'éthique."
      />

      {/* MODULE A */}
      <Section eyebrow="Module A" title="Le mindset du deal maker" index="A">
        <Quote text="Les deals ne se perdent pas dans les documents. Ils se perdent dans la tête." />
        <p>
          C'est le module que <Bold>99 % des formations n'ont pas</Bold>, et c'est celui qui fait la différence entre
          quelqu'un qui reste à zéro deal pendant 8 mois et quelqu'un qui ferme son premier deal en 6 semaines.
        </p>

        <Sub title="La différence entre un intermédiaire et un deal maker">
          <p>
            Un intermédiaire <em>attend</em>. Il envoie des emails, il reçoit des FCO, il transmet des LOI, et il espère.
            Un deal maker <em>crée l'opportunité</em>. Il structure le deal, il gère les parties, il impose son rythme,
            il prend des décisions sous pression. Un intermédiaire est passif dans la chaîne. Un deal maker est l'architecte
            de la chaîne.
          </p>
          <p>
            Cette distinction n'est pas une question de diplôme, d'argent de départ, ou de réseau hérité. C'est une question
            de <Bold>posture mentale</Bold>.
          </p>
        </Sub>

        <Sub title="La gestion de l'incertitude">
          <p>
            Le deal making est structurellement incertain. Vous pouvez travailler 3 mois sur un deal et le voir tomber à
            la dernière étape pour une raison indépendante de votre volonté. Un LC qui ne sort pas, un vendeur qui disparaît,
            un acheteur qui change ses priorités. Les débutants vivent ces situations comme des échecs personnels. Les deal
            makers les vivent comme des données du métier.
          </p>
          <p>
            La question à se poser : si vous travaillez 4 mois sans revenus et que votre premier deal tombe à la dernière
            étape, que se passe-t-il dans votre tête ? Si la réponse est « j'abandonne » ou « je doute de tout », vous
            n'êtes pas encore prêt pour ce métier. Le module mindset doit travailler cette résistance en amont.
          </p>
        </Sub>

        <Sub title="La gestion de l'imposteur">
          <p>
            La quasi-totalité des intermédiaires débutants vivent le syndrome de l'imposteur de manière intense. « Qui
            suis-je pour traiter des deals de millions de dollars ? » « Et si on me pose une question technique à laquelle
            je ne sais pas répondre ? » « Est-ce que les vrais acheteurs vont me prendre au sérieux ? »
          </p>
          <p>
            La réponse concrète : votre légitimité ne vient pas de vos diplômes ni de votre historique. Elle vient de votre
            <Bold> préparation</Bold>, de votre <Bold>professionnalisme documentaire</Bold>, et de votre <Bold>capacité
            à apporter une valeur réelle</Bold>. Les meilleurs traders mondiaux ont tous commencé sans réseau et sans
            track record. Glencore, aujourd'hui une entreprise de 250 milliards $, a été fondée par Marc Rich dans un
            appartement à New York.
          </p>
        </Sub>

        <Sub title="La patience stratégique">
          <p>
            Les deal makers qui réussissent ont une vision à <Bold>12-24 mois</Bold>, pas à 30 jours. Ils construisent
            des relations, des positions, des réputations. Ils ne cherchent pas le deal rapide. Ils cherchent le premier
            deal solide qui en générera dix autres.
          </p>
          <p>
            Un exercice concret : construire votre « deal pipeline » sur 18 mois avec des objectifs réalistes.
            <Bold> Mois 1-3 :</Bold> construction du réseau et qualification des contacts. <Bold>Mois 4-6 :</Bold> premier
            deal en cours. <Bold>Mois 7-12 :</Bold> premier deal fermé. <Bold>Mois 13-18 :</Bold> 2 à 3 deals actifs
            simultanément. Ce planning réduit la pression mensuelle et permet de tenir dans la durée.
          </p>
        </Sub>

        <Sub title="Le rapport à l'argent">
          <p>
            Beaucoup d'intermédiaires débutants sont intimidés par les montants en jeu. Quand vous négociez un deal de
            10 millions $, il faut être capable de parler de ces chiffres avec le même calme que si vous parliez de
            10 000 $. Les contreparties professionnelles le sentent immédiatement si vous êtes impressionné par les
            montants. Habituez-vous à prononcer les montants à voix haute, à les écrire, à les visualiser.
            <Bold> La normalisation psychologique des grands chiffres est une compétence qui s'entraîne.</Bold>
          </p>
        </Sub>
      </Section>

      {/* MODULE B */}
      <Section eyebrow="Module B" title="Personal Branding & Positionnement" index="B">
        <Quote text="Dans ce métier, vous êtes votre marque. Votre réputation vous précède dans chaque room." />

        <Sub title="Pourquoi le personal branding est critique en intermédiation">
          <p>
            Contrairement à d'autres secteurs où l'entreprise efface l'individu, l'intermédiation internationale est un
            métier de personne à personne. Quand quelqu'un décide de travailler avec vous sur un deal de plusieurs
            millions de dollars, il ne choisit pas votre entreprise — <Bold>il vous choisit, vous</Bold>. Votre réputation,
            votre crédibilité, votre réseau personnel sont vos seuls actifs réels.
          </p>
        </Sub>

        <Sub title="Définir son positionnement">
          <p>La première étape est de répondre honnêtement à ces trois questions :</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Sur quels produits ou secteurs êtes-vous vraiment compétent ?</li>
            <li>Sur quelles zones géographiques avez-vous des connexions réelles ou souhaitez-vous en construire ?</li>
            <li>Quel problème précis résolvez-vous pour vos clients ?</li>
          </ol>
          <Callout title="Test de la phrase de positionnement">
            <p className="text-[#A1A1AA]">❌ « Je suis intermédiaire en commodités internationales » — phrase que personne ne retient et qui ne différencie personne.</p>
            <p className="text-[#F5F5DC] mt-3">✅ « Je connecte des raffineries de sucre brésiliennes à des acheteurs industriels en Afrique de l'Ouest avec une expertise documentaire et Halal. » — phrase qui crée une position.</p>
          </Callout>
        </Sub>

        <Sub title="Construire une présence LinkedIn professionnelle">
          <p>
            LinkedIn est le seul réseau social qui compte vraiment dans le commerce international B2B. Voici la structure
            optimale pour un intermédiaire en commodités :
          </p>
          <ul className="space-y-3">
            <li><Bold>Photo professionnelle :</Bold> pas de selfie de vacances. Fond neutre, vêtement professionnel,
            regard direct. C'est votre première impression pour des milliers de professionnels.</li>
            <li><Bold>Titre :</Bold> « Commodity Broker | [Secteur spécifique] | [Zone géographique] ». Exemples :
            « Soft Commodities Broker | Sugar, Coffee, Cocoa | Africa & Middle East » ou « Industrial Metals Trader |
            Copper, Aluminium | West Africa ».</li>
            <li><Bold>Section "About" :</Bold> 3 à 5 phrases maximum, en anglais. Qui vous êtes, sur quoi vous travaillez,
            ce que vous apportez concrètement, comment vous contacter. Pas de jargon inutile.</li>
            <li><Bold>Expériences :</Bold> même si vous débutez, présentez vos formations, votre compréhension du secteur,
            vos projets en cours.</li>
            <li><Bold>Contenu publié :</Bold> publiez régulièrement des analyses de marché courtes (prix du cacao cette
            semaine et pourquoi, impact d'El Niño sur le prix du café, etc.). Ce contenu vous positionne comme expert et
            attire des contacts qualifiés.</li>
          </ul>
        </Sub>

        <Sub title="L'email signature comme outil de branding">
          <p>
            Votre signature email est lue des milliers de fois. Elle doit projeter professionnalisme et spécialisation.
            Évitez les signatures génériques. Mentionnez votre spécialité, vos zones de couverture, et si possible une
            ligne de crédibilité (« Member of [organisation professionnelle] »).
          </p>
        </Sub>

        <Sub title="Votre présence en ligne au-delà de LinkedIn">
          <p>
            Un site web simple mais professionnel est un atout (une seule page avec votre positionnement, vos services,
            vos coordonnées). Pas besoin d'un site complexe. Un WordPress ou Squarespace sobre fait l'affaire.
          </p>
          <p>
            Un email professionnel avec votre nom de domaine (@votre-société.com) est <Bold>indispensable</Bold>. Un
            email Gmail ou Yahoo dans des deals de millions de dollars détruit immédiatement votre crédibilité.
          </p>
        </Sub>
      </Section>

      {/* MODULE C */}
      <Section eyebrow="Module C" title="Construction de réseau (Networking stratégique)" index="C">
        <Quote text="En intermédiation, votre réseau n'est pas votre atout. Votre réseau EST votre business." />

        <Sub title="La différence entre contacts et réseau">
          <p>
            Avoir 5 000 connexions LinkedIn n'est pas un réseau. Un réseau, c'est un ensemble de personnes qui vous
            connaissent, vous font confiance, et sont prêtes à vous introduire ou à travailler avec vous.
            <Bold> 50 vrais contacts valent 5 000 connexions superficielles.</Bold>
          </p>
        </Sub>

        <Sub title="Les 3 cercles du réseau d'un deal maker">
          <p>
            <Bold>Cercle 1 — Contacts directs (les plus précieux) :</Bold> personnes avec qui vous avez travaillé, eu un
            échange significatif, ou une relation personnelle. Ils vous répondent dans les 24 heures. Visez 20 à 50
            contacts de ce niveau dans votre secteur de spécialité.
          </p>
          <p>
            <Bold>Cercle 2 — Contacts de second niveau :</Bold> personnes qui vous connaissent de nom ou par introduction.
            Ils vous répondent si vous les approchez de manière personnalisée. Potentiel de passage au Cercle 1 avec du
            temps et des interactions.
          </p>
          <p>
            <Bold>Cercle 3 — Communauté élargie :</Bold> votre réseau LinkedIn, vos abonnés, les membres de groupes
            professionnels où vous êtes actif. Réservoir dans lequel vous puisez pour trouver des contacts Cercle 2.
          </p>
        </Sub>

        <Sub title="Où trouver les bons contacts">
          <p>
            <Bold>Salons professionnels internationaux :</Bold> c'est là que se font les vraies connexions dans le monde
            des commodités. Les incontournables : Gulfood (Dubaï — agroalimentaire, sucre, huiles), World Coffee Conference
            (organisé par l'ICO), Salon du Chocolat Professional (Paris — cacao), Mining Indaba (Cape Town — mines et
            métaux), Africa CEO Forum, ADIPEC (Abu Dhabi — pétrole et énergie), Metal Bulletin International Aluminium
            Conference, <Bold>LME Week</Bold> (Londres, chaque octobre — la semaine la plus importante du calendrier pour
            les métaux industriels).
          </p>
          <p>
            <Bold>Associations professionnelles :</Bold> FOSFA, GAFTA, ICO (International Coffee Organization), ICCO
            (International Cocoa Organization), ISO (International Sugar Organization), LME member community, CRU Group events.
          </p>
          <p>
            <Bold>Plateformes digitales spécialisées :</Bold> Fastmarkets (anciennement Metal Bulletin) pour les métaux,
            IHS Markit pour les énergies, Agrimoney pour les soft commodities, S&P Global Platts community.
          </p>
        </Sub>

        <Sub title="Comment approcher un contact inconnu de manière efficace">
          <p>
            La règle d'or : <Bold>personnalisez toujours</Bold>. Un message générique reçoit une réponse générique ou pas
            de réponse du tout.
          </p>
          <p>Structure d'un premier message efficace sur LinkedIn ou par email :</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Accroche personnalisée (référence à un article qu'ils ont publié, une position commune, un événement)</li>
            <li>Une phrase sur vous (ultra-courte, ultra-spécifique)</li>
            <li>La valeur que vous pouvez apporter ou l'information que vous cherchez</li>
            <li>Un appel à l'action simple et non-intrusif</li>
          </ol>
          <Callout title="Exemple">
            <p className="italic text-[#F5F5DC]">
              « Bonjour [Nom], j'ai lu votre article sur l'évolution du marché du cacao ghanéen — votre analyse sur l'impact
              EUDR était particulièrement pertinente. Je travaille sur la mise en relation d'exportateurs ghanéens avec
              des chocolatiers européens en recherche de traçabilité certifiée. Seriez-vous ouvert à un échange de 20
              minutes cette semaine ou la suivante ? »
            </p>
          </Callout>
        </Sub>

        <Sub title="Entretenir son réseau">
          <p>
            La pire erreur en networking : <Bold>ne contacter les gens que quand on a besoin d'eux</Bold>. Un réseau solide
            se construit dans la durée par des interactions régulières sans agenda immédiat. Partagez des informations
            pertinentes avec vos contacts, félicitez-les pour leurs succès, commentez leurs publications, envoyez des
            articles qui les intéresseraient. Quand vous aurez besoin d'eux pour un deal, le terrain sera préparé.
          </p>
        </Sub>
      </Section>

      {/* MODULE D */}
      <Section eyebrow="Module D" title="L'art de closer un deal" index="D">
        <Quote text="Tout le monde peut trouver un deal. Les rares savent le fermer." />
        <p>
          C'est le module le plus opérationnel et souvent le plus négligé. Des centaines d'intermédiaires ont des
          portefeuilles remplis de deals « en cours » qui n'aboutissent jamais parce qu'ils ne savent pas comment pousser
          au closing.
        </p>

        <Sub title="Comprendre pourquoi les deals meurent">
          <p>
            Statistique dure à entendre : dans le commerce international de commodités, environ <Bold>70 % à 80 % des deals
            initiés n'aboutissent jamais</Bold>. Les principales raisons de mort d'un deal :
          </p>
          <ul className="space-y-2 ml-4">
            <li className="flex gap-3"><span className="text-[#FF3B30] mt-2">▸</span><span>Deal jamais sérieux dès le départ (vendeur ou acheteur fictif ou non qualifié)</span></li>
            <li className="flex gap-3"><span className="text-[#FF3B30] mt-2">▸</span><span>Trop d'intermédiaires dans la chaîne qui diluent l'information et la confiance</span></li>
            <li className="flex gap-3"><span className="text-[#FF3B30] mt-2">▸</span><span>Problème de financement non identifié en amont (l'acheteur ne peut pas ouvrir le LC)</span></li>
            <li className="flex gap-3"><span className="text-[#FF3B30] mt-2">▸</span><span>Désaccord sur les termes au moment de la rédaction du contrat</span></li>
            <li className="flex gap-3"><span className="text-[#FF3B30] mt-2">▸</span><span>Perte de momentum (le deal prend trop de temps et les parties se désintéressent)</span></li>
            <li className="flex gap-3"><span className="text-[#FF3B30] mt-2">▸</span><span>Changement des prix de marché qui rend le deal non rentable</span></li>
          </ul>
        </Sub>

        <Sub title="La qualification : la compétence la plus sous-estimée">
          <p>Avant de passer une seconde sur un deal, un deal maker pose les questions de qualification suivantes :</p>
          <p><Bold>À l'acheteur :</Bold></p>
          <ul className="space-y-1 ml-4 italic">
            <li>« Êtes-vous le décideur final ou devez-vous obtenir une approbation ? »</li>
            <li>« Avez-vous une ligne de crédit ou une LC facility ouverte avec votre banque ? »</li>
            <li>« Quel est votre délai de livraison idéal ? »</li>
            <li>« Avez-vous déjà acheté ce produit auparavant ? »</li>
            <li>« Quel est votre volume exact et votre budget ? »</li>
          </ul>
          <p><Bold>Au vendeur :</Bold></p>
          <ul className="space-y-1 ml-4 italic">
            <li>« Êtes-vous le mandataire direct ou travaillez-vous pour quelqu'un d'autre ? »</li>
            <li>« Le produit est-il disponible maintenant ? »</li>
            <li>« Avez-vous un certificat de disponibilité ou un warehouse receipt ? »</li>
            <li>« Combien de temps votre offre est-elle valable ? »</li>
          </ul>
          <p>
            Si les réponses sont vagues, évasives, ou si les documents de base ne peuvent pas être fournis dans les 48
            heures, <Bold>sortez</Bold>. La qualification rigoureuse est ce qui sépare les deal makers qui réussissent de
            ceux qui perdent des mois sur des fantômes.
          </p>
        </Sub>

        <Sub title="Créer et maintenir le momentum">
          <p>
            Le momentum est la dynamique de progression d'un deal. Un deal avec momentum avance d'une étape à l'autre avec
            des délais clairs et respectés. Un deal sans momentum stagne, les parties se déconcentrent, et il finit par mourir.
          </p>
          <p>
            <Bold>Comment créer le momentum :</Bold> définissez des next steps concrets à la fin de chaque échange. Jamais
            « on se rappelle bientôt » mais « je vous envoie le NCNDA d'ici ce soir, vous me confirmez votre signature
            pour demain 17h ». Toute interaction doit se terminer par une action concrète avec une deadline.
          </p>
          <p>
            <Bold>Comment relancer un deal qui stagne :</Bold> envoyez une information de marché pertinente pour relancer
            l'échange (« Je vous envoie ceci parce que le prix LME a bougé de 3 % cette semaine, ce qui impacte notre
            pricing — avez-vous pu soumettre notre proposition à votre direction ? »).
          </p>
        </Sub>

        <Sub title="Les 5 étapes du closing">
          <p>
            <Bold>Étape 1 — Le Test Fermé :</Bold> avant de formaliser quoi que ce soit, testez l'engagement des parties.
            « Si on s'accorde sur le prix et les termes de livraison, êtes-vous prêt à signer le contrat cette semaine ? »
            La réponse à cette question révèle immédiatement si la partie est réellement engagée ou juste en train de
            collecter de l'information.
          </p>
          <p>
            <Bold>Étape 2 — La Résolution des Objections :</Bold> listez et adressez toutes les objections AVANT de passer
            au contrat. Une objection non résolue devient un blocage rédhibitoire au dernier moment.
          </p>
          <p>
            <Bold>Étape 3 — Le Récapitulatif Écrit :</Bold> envoyez un email de synthèse des termes convenus avant de
            rédiger le contrat formel. « Voici ce que nous avons convenu : produit X, quantité Y, prix Z, livraison CIF
            [port] en [date], paiement par LC à vue. Confirmez-vous ces termes ? » Cet email crée un engagement moral fort
            avant le contrat légal.
          </p>
          <p>
            <Bold>Étape 4 — Le Contrat et la Signature :</Bold> rédigez le contrat le plus vite possible après l'accord
            de principe. Chaque heure qui passe donne à une partie le temps de changer d'avis, de trouver une alternative,
            ou simplement de se refroidir.
          </p>
          <p>
            <Bold>Étape 5 — Le Suivi Post-Signature :</Bold> le deal n'est pas fermé à la signature du contrat. Il est
            fermé quand l'argent est reçu et les commissions versées. Restez actif et présent jusqu'au paiement final.
          </p>
        </Sub>
      </Section>

      {/* MODULE E */}
      <Section eyebrow="Module E" title="Structuration avancée des deals" index="E">
        <Quote text="Les deal makers simples apportent des acheteurs et des vendeurs. Les deal makers avancés créent des structures que personne d'autre ne peut imaginer." />

        <Sub title="Le financement du commerce (Trade Finance)">
          <p>
            La majorité des deals internationaux ne tombent pas à cause d'un problème de produit ou de prix. Ils tombent
            à cause d'un problème de financement. L'acheteur veut livraison avant paiement. Le vendeur veut paiement avant
            livraison. Le deal maker avancé connaît les instruments qui permettent de réconcilier ces deux positions.
          </p>
          <p>
            <Bold>Financement de préfinancement (Pre-Export Finance / PXF) :</Bold> une banque finance le vendeur AVANT
            l'exportation, sur la base du contrat et du LC de l'acheteur. Cela permet au vendeur de produire ou d'acheter
            le produit sans fonds propres.
          </p>
          <p>
            <Bold>Financement de stock (Warehouse Financing / Inventory Financing) :</Bold> le métal ou la commodité
            stockée dans un entrepôt agréé est utilisée comme collatéral pour obtenir un financement. Un deal maker qui
            maîtrise cette technique peut débloquer des transactions que personne d'autre ne peut financer.
          </p>
          <p>
            <Bold>Discounting de LC (LC Discounting) :</Bold> la banque du vendeur « achète » la créance représentée par
            le LC à terme à un prix légèrement inférieur à la valeur nominale, donnant au vendeur un accès immédiat aux
            fonds sans attendre l'échéance.
          </p>
          <p>
            <Bold>Forfaiting :</Bold> le vendeur cède (vend) ses créances à terme sur l'acheteur à une institution
            financière spécialisée, sans recours. Utilisé pour les deals de grands montants avec des délais de paiement
            longs (180 jours à 5 ans).
          </p>
        </Sub>

        <Sub title="Les structures complexes multi-parties">
          <p>
            Un deal maker avancé peut structurer des opérations impliquant 3, 4 ou 5 parties simultanément.
            <Bold> Exemple concret :</Bold> un fonds souverain africain veut acheter 500 000 tonnes de blé ukrainien pour
            ses réserves stratégiques. Il n'a pas les relations directes avec les exportateurs ukrainiens. Un intermédiaire
            européen a accès aux exportateurs ukrainiens mais n'a pas de relations avec le fonds africain. Une banque
            d'affaires structure le financement. Un opérateur logistique gère le fret. L'intermédiaire avancé orchestre
            l'ensemble de ces parties, protège sa position à chaque niveau, et structure les commissions de manière à ce
            que tout le monde soit aligné.
          </p>
        </Sub>

        <Sub title="Les tolling agreements">
          <p>
            Un tolling agreement est un contrat dans lequel le propriétaire d'une matière première (ex. : bauxite) confie
            sa transformation (ex. : en aluminium) à une raffinerie contre paiement d'une « tolle » (frais de transformation),
            sans vente de la matière première. L'intermédiaire qui comprend et propose ce type de structure accède à des
            deals inaccessibles pour les autres.
          </p>
        </Sub>

        <Sub title="Les swaps de commodités">
          <p>
            Deux parties échangent des quantités équivalentes de la même commodité mais dans des géographies différentes
            pour des raisons logistiques. Exemple : une société américaine a du cuivre à Detroit qu'elle veut à Rotterdam,
            et une société européenne a du cuivre à Rotterdam qu'elle veut à Detroit. Un swap évite à chacun un transport
            intercontinental coûteux. L'intermédiaire qui identifie et structure ce type d'opportunité crée de la valeur
            pure sans même toucher à la marchandise.
          </p>
        </Sub>
      </Section>

      {/* MODULE F */}
      <Section eyebrow="Module F" title="Intelligence des marchés & veille stratégique" index="F">
        <Quote text="L'information, dans le trading de commodités, vaut de l'or. Parfois littéralement." />

        <Sub title="Comprendre les rapports de marché fondamentaux">
          <p>
            <Bold>USDA WASDE Report (World Agricultural Supply and Demand Estimates) :</Bold> publié le deuxième vendredi
            de chaque mois. C'est le rapport le plus attendu du marché agricole mondial. Il donne les estimations de
            production, consommation, stocks et échanges pour toutes les grandes commodités agricoles (blé, maïs, soja,
            coton, sucre, café, oléagineux). La publication de ce rapport crée souvent des mouvements de prix immédiats
            et significatifs. Un deal maker qui sait lire et interpréter ce rapport avant ses concurrents a un avantage décisif.
          </p>
          <p>
            <Bold>CFTC COT Report (Commitments of Traders) :</Bold> publié chaque vendredi pour les données du mardi
            précédent. Révèle les positions des différentes catégories de traders (commerciaux, non-commerciaux, petits
            spéculateurs) sur les contrats à terme. Quand les spéculateurs sont excessivement positionnés d'un côté, un
            retournement de prix est souvent imminent.
          </p>
          <p>
            <Bold>LME Inventory Reports :</Bold> publiés quotidiennement, ils révèlent les stocks de métaux dans les
            entrepôts agréés LME. Une chute rapide des stocks (cancelled warrants en hausse) signale une tension sur
            l'offre physique et annonce généralement une hausse des prix.
          </p>
          <p>
            <Bold>Baltic Dry Index (BDI) :</Bold> indicateur du coût du fret maritime en vrac sec. Publié quotidiennement
            par le Baltic Exchange. Une hausse du BDI augmente les prix CIF et peut changer la rentabilité d'un deal.
          </p>
        </Sub>

        <Sub title="Suivre les décisions des banques centrales">
          <p>
            Les prix de toutes les commodités cotées en USD sont influencés par la politique monétaire américaine (Fed).
            Quand la Fed monte ses taux, le dollar se renforce, et les commodités en USD baissent mécaniquement en valeur.
            Quand la Fed baisse ses taux, le dollar s'affaiblit et les commodités montent. Un intermédiaire qui intègre
            cette dynamique dans son analyse peut mieux anticiper les mouvements de prix.
          </p>
        </Sub>

        <Sub title="Comprendre les flux géopolitiques">
          <p>
            Certains événements géopolitiques ont un impact direct et immédiat sur les marchés de commodités. La guerre
            en Ukraine a fait exploser les prix du blé et du maïs en 2022. Les sanctions contre la Russie ont transformé
            le marché mondial de l'aluminium et du nickel. Un coup d'État en Afrique de l'Ouest peut perturber les
            exportations de cacao. Une élection présidentielle en Brésil peut changer la politique agricole et les prix
            du sucre.
          </p>
          <p>
            Le deal maker qui anticipe ces événements ou réagit le plus vite possible après qu'ils se produisent crée des
            opportunités là où les autres voient du chaos.
          </p>
        </Sub>

        <Sub title="Construire sa propre veille">
          <p>Outils de veille indispensables pour un deal maker en commodités :</p>
          <ul className="space-y-2 ml-4">
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>Fastmarkets</Bold> (fastmarkets.com) : prix et analyses pour les métaux, la forêt, et l'agriculture.</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>S&P Global Platts</Bold> (spglobal.com) : pétrole, gaz, pétrochimie, métaux, agriculture.</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>Reuters Commodities</Bold> (reuters.com/markets/commodities) : nouvelles de marché en temps réel, gratuit.</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>Agrimoney</Bold> (agrimoney.com) : focus agriculture et soft commodities.</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>Metal Bulletin</Bold> (metalinsider.com) : métaux industriels et ferreux.</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>ICO Coffee Report</Bold> (ico.org) : statistiques et analyses café.</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>ICCO Quarterly Bulletin</Bold> : cacao.</span></li>
          </ul>
          <p>
            <Bold>Newsletters gratuites à suivre absolument :</Bold> USDA FAS (Foreign Agricultural Service), FAO Food
            Outlook, World Bank Commodity Markets Outlook (trimestriel).
          </p>
        </Sub>
      </Section>

      {/* MODULE G */}
      <Section eyebrow="Module G" title="Droit des contrats internationaux" index="G">
        <Quote text="Un deal verbal est du vent. Un deal mal documenté est une bombe à retardement." />

        <Sub title="La Convention de Vienne (CISG)">
          <p>
            La Convention des Nations Unies sur les Contrats de Vente Internationale de Marchandises (CISG), signée à
            Vienne en 1980, est le cadre juridique international de référence pour les contrats de vente de marchandises.
            Elle a été ratifiée par <Bold>plus de 95 pays représentant plus de 80 % du commerce mondial</Bold>. Tout
            intermédiaire doit comprendre ses principes fondamentaux.
          </p>
          <p>
            La CISG s'applique automatiquement quand les deux parties d'un contrat sont dans des pays signataires et
            qu'elles n'ont pas exclu son application. Elle définit les règles de formation du contrat, les obligations
            du vendeur et de l'acheteur, les recours en cas de non-conformité, les règles sur le transfert des risques.
          </p>
        </Sub>

        <Sub title="Les clauses essentielles à ne jamais oublier">
          <p>
            <Bold>Clause de force majeure :</Bold> définit les événements hors du contrôle des parties (guerre, catastrophe
            naturelle, pandémie, grève générale) qui peuvent suspendre ou annuler les obligations contractuelles sans
            pénalité. La rédaction de cette clause est devenue critique depuis COVID-19 et la guerre en Ukraine.
          </p>
          <p>
            <Bold>Clause de hardship (imprévision) :</Bold> différente de la force majeure, elle permet la renégociation
            du contrat si un changement de circonstances (ex. : hausse brutale des prix) rend l'exécution excessivement
            onéreuse pour une partie, sans pour autant la rendre impossible.
          </p>
          <p>
            <Bold>Clause de droit applicable :</Bold> définit quel droit national gouverne l'interprétation du contrat.
            Exemple : « Le présent contrat est régi par le droit anglais. » Le droit anglais est le plus souvent choisi
            dans les contrats de commodités car il est prévisible, bien documenté, et universellement reconnu.
          </p>
          <p>
            <Bold>Clause d'arbitrage :</Bold> définit comment les litiges seront résolus. L'arbitrage est très préféré
            aux tribunaux nationaux dans les deals internationaux car il est généralement plus rapide, confidentiel, et
            les sentences sont exécutables dans <Bold>164 pays grâce à la Convention de New York (1958)</Bold>. Les
            centres d'arbitrage de référence sont la CCI (Chambre de Commerce Internationale, Paris), le LCIA (London
            Court of International Arbitration), le GAFTA (pour les céréales), et le FOSFA (pour les huiles et graisses).
          </p>
        </Sub>

        <Sub title="La Lettre de Crédit documentaire — Maîtrise avancée">
          <p>Au-delà de la vérification basique (Module 3), un deal maker avancé comprend les subtilités :</p>
          <p>
            <Bold>La conformité stricte (Strict Compliance) :</Bold> en LC documentaire, un retard d'un jour, une virgule
            mal placée, ou une abréviation non conforme dans les documents peut entraîner un refus de paiement par la
            banque. Les banques ne jugent pas les faits (est-ce que la marchandise a bien été livrée ?), elles jugent
            les documents (est-ce que les documents sont conformes aux termes exacts de la LC ?).
          </p>
          <p>
            <Bold>Les réserves documentaires (Discrepancies) :</Bold> quand des documents présentent des écarts par rapport
            aux termes du LC, la banque peut émettre des « discrepancies » et refuser de payer jusqu'à leur résolution.
            Un deal maker expérimenté connaît les discrepancies les plus courantes et les évite en préparant les documents
            avec soin.
          </p>
          <p>
            <Bold>La LC transférable (Transferable LC) :</Bold> permet à l'intermédiaire de transférer tout ou partie du
            bénéfice à un fournisseur tiers. C'est l'outil qui permet à un intermédiaire de structurer un deal back-to-back
            sans avoir besoin de fonds propres.
          </p>
          <p>
            <Bold>La LC stand-by (SBLC) :</Bold> fonctionne comme une garantie bancaire. Elle n'est « tirée » que si le
            bénéficiaire prouve que l'autre partie a failli à ses obligations. Différent fondamentalement d'un LC
            documentaire classique qui est « tiré » sur présentation de documents conformes.
          </p>
        </Sub>
      </Section>

      {/* MODULE H */}
      <Section eyebrow="Module H" title="Gestion de crise & résolution de litiges" index="H">
        <Quote text="Ce que vous faites quand le deal tourne mal définit votre niveau de professionnel." />

        <Sub title="Crise 1 — Le vendeur livre un produit non conforme">
          <p>
            La marchandise arrive au port de destination et l'inspection de réception révèle que la qualité ne correspond
            pas aux spécifications du contrat (aluminium Grade B livré au lieu de Grade A, sucre à ICUMSA 200 au lieu de
            ICUMSA 45, café avec un score SCA de 78 au lieu des 83 promis).
          </p>
          <p>
            <Bold>Protocole de gestion :</Bold> ne rien signer qui accepterait la marchandise telle quelle. Faire
            immédiatement réaliser une contre-expertise par SGS ou Bureau Veritas avec un échantillon contra-signé.
            Notifier le vendeur par écrit dans les délais contractuels (souvent 48 à 72 heures après déchargement).
            Documenter tous les préjudices (coûts de stockage supplémentaires, manque à gagner). Proposer d'abord une
            résolution amiable (décote sur le prix) avant d'activer la clause d'arbitrage.
          </p>
        </Sub>

        <Sub title="Crise 2 — L'acheteur refuse de payer">
          <p>
            Le vendeur a livré conformément, les documents sont en ordre, mais l'acheteur invoque des raisons diverses
            pour ne pas payer ou retarder le paiement.
          </p>
          <p>
            <Bold>Protocole de gestion :</Bold> si le paiement était via LC, la banque émettrice est <Bold>obligée de
            payer</Bold> sur présentation de documents conformes — c'est la sécurité fondamentale du LC. Si le paiement
            était en TT open account, la situation est plus complexe : mise en demeure formelle, puis activation de la
            clause d'arbitrage ou saisine d'un tribunal selon le droit applicable.
          </p>
        </Sub>

        <Sub title="Crise 3 — Le vendeur disparaît après paiement">
          <p>
            Dans les soft commodities particulièrement, des cas de fournisseurs qui reçoivent un acompte et disparaissent
            ou livrent une fraction de la quantité commandée existent.
          </p>
          <p>
            <Bold>Protocole de gestion :</Bold> ne JAMAIS verser d'acompte sans garantie bancaire (SBLC ou LC). Si c'est
            arrivé, contacter immédiatement les autorités locales du pays du vendeur (police commerciale, chambre de
            commerce), un avocat local, et éventuellement une société de recouvrement internationale.
          </p>
        </Sub>

        <Sub title="L'arbitrage international — Processus pratique">
          <p>
            Quand la résolution amiable échoue, l'arbitrage est la voie royale. Un deal maker doit comprendre ce processus
            pour ne pas être pris au dépourvu.
          </p>
          <p>
            <Bold>Initiation :</Bold> dépôt d'une demande d'arbitrage auprès de l'institution choisie (CCI, LCIA, GAFTA,
            FOSFA) avec description du litige et montant réclamé. Frais d'initiation : de 2 000 $ à 15 000 $ selon
            l'institution et le montant.
          </p>
          <p>
            <Bold>Constitution du tribunal :</Bold> nomination d'un ou trois arbitres (selon le règlement et le montant
            en jeu). Les arbitres doivent être indépendants et impartiaux.
          </p>
          <p>
            <Bold>Échange de mémoires :</Bold> chaque partie soumet ses arguments écrits, preuves documentaires, et
            témoignages d'experts.
          </p>
          <p><Bold>Audience :</Bold> une ou plusieurs audiences en présentiel ou en visioconférence.</p>
          <p><Bold>Sentence :</Bold> l'arbitre ou le panel rend une sentence finale, obligatoire et exécutoire.</p>
          <p>
            <Bold>Durée :</Bold> de 6 mois à 2 ans selon la complexité. <Bold>Coût total :</Bold> de 15 000 $ à plusieurs
            centaines de milliers de dollars.
          </p>
          <p>
            <Bold>Exécution de la sentence :</Bold> en vertu de la Convention de New York de 1958, une sentence arbitrale
            internationale est exécutoire dans 164 pays. C'est son avantage majeur sur un jugement de tribunal national.
          </p>
        </Sub>
      </Section>

      {/* MODULE I */}
      <Section eyebrow="Module I" title="Gestion financière du deal maker" index="I">
        <Quote text="Vous pouvez faire des millions de commissions et être ruiné si vous ne gérez pas votre argent." />

        <Sub title="Le cash flow du deal maker indépendant">
          <p>
            Le deal making a un profil de cash flow très particulier : zéro revenu pendant plusieurs mois, puis
            potentiellement une grosse commission sur un deal fermé. Ce profil en <Bold>« dents de scie »</Bold> est
            psychologiquement et financièrement difficile à gérer pour qui n'y est pas préparé.
          </p>
          <Callout title="Règle fondamentale">
            <p>
              Avoir toujours au minimum <Bold>6 mois de dépenses personnelles en réserve</Bold> avant de se lancer à
              plein temps dans l'intermédiation. Idéalement <Bold>12 mois</Bold>. Cette réserve vous enlève la pression
              psychologique du revenu immédiat, ce qui vous rend paradoxalement plus efficace dans vos négociations
              (on négocie mieux quand on n'a pas besoin du deal).
            </p>
          </Callout>
        </Sub>

        <Sub title="Gérer plusieurs deals simultanément">
          <p>
            Un deal maker confirmé gère simultanément plusieurs deals à différents stades de maturité. L'analogie du
            pipeline de vente est parfaite : vous avez besoin d'un pipeline assez plein pour que la probabilité statistique
            vous garantisse un deal fermé chaque trimestre. Si vous avez 10 deals en cours avec 10 % de probabilité chacun,
            vous avez statistiquement 1 deal fermé. Si vous avez 3 deals en cours, vous avez statistiquement 0,3 deal fermé.
          </p>
        </Sub>

        <Sub title="Tracker ses commissions avec précision">
          <p>
            Un deal maker doit tenir un tableau de bord de ses commissions attendues : nom du deal, montant total de la
            transaction, % de commission, commission attendue en valeur absolue, probabilité estimée de closing (10 %,
            30 %, 50 %, 80 %, 95 %), commission pondérée (montant × probabilité). Ce tableau donne une vision réaliste
            de votre pipeline financier.
          </p>
          <div className="border border-white/10 overflow-hidden mt-6">
            <table className="w-full text-sm">
              <thead className="bg-[#141414] text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                <tr><th className="text-left p-3">Deal</th><th className="text-left p-3">Montant</th><th className="text-left p-3">% comm.</th><th className="text-left p-3">Commission attendue</th><th className="text-left p-3">Probabilité</th><th className="text-left p-3">Pondérée</th></tr>
              </thead>
              <tbody className="divide-y divide-white/5 font-mono text-xs">
                <tr><td className="p-3 text-white">Or Mali</td><td className="p-3">2 M$</td><td className="p-3 text-[#D4AF37]">1 %</td><td className="p-3">20 k$</td><td className="p-3">80 %</td><td className="p-3 text-[#25D366]">16 k$</td></tr>
                <tr><td className="p-3 text-white">Cuivre Pérou</td><td className="p-3">15 M$</td><td className="p-3 text-[#D4AF37]">0,5 %</td><td className="p-3">75 k$</td><td className="p-3">30 %</td><td className="p-3 text-[#25D366]">22,5 k$</td></tr>
                <tr><td className="p-3 text-white">Sucre Brésil</td><td className="p-3">60 M$</td><td className="p-3 text-[#D4AF37]">0,75 %</td><td className="p-3">450 k$</td><td className="p-3">10 %</td><td className="p-3 text-[#25D366]">45 k$</td></tr>
                <tr className="bg-[#141414]"><td className="p-3 text-[#D4AF37]" colSpan="5"><strong>Total pipeline pondéré</strong></td><td className="p-3 text-[#D4AF37] font-bold">83,5 k$</td></tr>
              </tbody>
            </table>
          </div>
        </Sub>

        <Sub title="Réinvestir intelligemment">
          <p>
            Les premières commissions importantes sont souvent mal utilisées : dépenses ostentatoires, sous-investissement
            dans le développement du business. Un deal maker stratégique réinvestit dans :
          </p>
          <ul className="space-y-2 ml-4">
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>Son réseau</Bold> : voyages pour rencontrer des partenaires, participation aux salons clés (LME Week, Gulfood…)</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>Ses outils</Bold> : abonnements à des bases de données de marché, CRM premium</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>Sa formation continue</Bold> : cours, certifications, mentoring</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>Ses réserves de trésorerie</Bold> pour tenir entre les deals sans pression</span></li>
          </ul>
        </Sub>
      </Section>

      {/* MODULE J */}
      <Section eyebrow="Module J" title="Éthique, conformité et réputation" index="J">
        <Quote text="Dans un monde où tout se sait, votre intégrité est votre capital le plus précieux et le plus difficile à reconstruire." />

        <Sub title="Pourquoi l'éthique est une stratégie, pas seulement une valeur">
          <p>
            Dans le commerce international de commodités, les réseaux sont petits et les informations circulent vite. Un
            intermédiaire qui a arnaqué, trompé, ou simplement mal géré un deal sera identifié et mis en liste noire par
            les acteurs sérieux du secteur en quelques semaines. Sa réputation le précédera dans chaque nouvelle négociation.
          </p>
          <p>
            À l'inverse, un intermédiaire réputé pour son honnêteté, sa précision, et sa fiabilité verra son réseau lui
            envoyer des deals sans qu'il ait à les chercher. <Bold>La réputation est le seul actif qui travaille pour vous
            même quand vous dormez.</Bold>
          </p>
        </Sub>

        <Sub title="Les situations éthiquement grises que tout intermédiaire rencontre">
          <p>
            <Bold>Situation 1 — Un acheteur vous demande de ne pas révéler que vous prenez une commission.</Bold> C'est
            parfaitement légal (commission fermée) à condition que vous ne trompiez pas l'acheteur sur le prix qu'il
            paierait sans vous.
          </p>
          <p>
            <Bold>Situation 2 — Vous découvrez en cours de deal que le vendeur a de légères problématiques de conformité</Bold>
            (pas de fraude caractérisée mais des zones grises documentaires). Que faire ? La réponse éthique et stratégique
            est de soulever le problème ouvertement et de proposer une solution, pas de fermer les yeux.
          </p>
          <p>
            <Bold>Situation 3 — Une partie vous offre une commission supérieure si vous les aidez à exclure un autre
            intermédiaire de la chaîne</Bold> qui a pourtant contribué au deal. La tentation est forte. La réponse correcte
            est <Bold>non</Bold> — votre réputation d'équité vaut plus que la commission supplémentaire.
          </p>
        </Sub>

        <Sub title="Les obligations légales de conformité">
          <p>
            <Bold>Anti-Money Laundering (AML) :</Bold> même en tant qu'intermédiaire indépendant, vous pouvez être tenu
            responsable si vous facilitez sciemment des transactions liées au blanchiment d'argent. La règle est simple :
            <Bold> si vous ne pouvez pas expliquer de manière satisfaisante l'origine des fonds de votre acheteur, ne faites
            pas le deal</Bold>.
          </p>
          <p>
            <Bold>Anti-Bribery & Corruption :</Bold> dans de nombreux pays (UK Bribery Act 2010, US Foreign Corrupt
            Practices Act), offrir ou accepter des pots-de-vin est un crime, même si cela se passe dans un pays tiers.
            Des commissions « inhabituelles » versées à des agents locaux de pays à risque de corruption peuvent être
            assimilées à de la corruption.
          </p>
          <p>
            <Bold>Sanctions internationales :</Bold> comme vu dans le Module 3, travailler avec des entités sanctionnées
            vous expose à des poursuites graves, même si vous n'êtes pas ressortissant américain ou européen.
          </p>
        </Sub>
      </Section>
    </div>
  );
}
